import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import _intersection from 'lodash/intersection';
import WatchPropTypes from 'common/PropTypes';
import ScheduleModuleContainer from '@alias/ScheduleModuleContainer';
import { BucketLayout, BucketStatus, PageType } from 'enums';
import Bucket from './Bucket';
import { bamSubReadySelector } from 'common/selectors/authenticationSelectors';
import pageSelector, { showPromoSelector } from 'common/selectors/pageSelector';

export function insertScheduleModuleIntoBuckets(buckets, pageType) {
    const clonedBuckets = buckets.slice();
    // on the home page, find the live bucket, and insert the schedule module right after it
    if (pageType === PageType.HOME) {
        let insertIndex = -1;
        buckets.forEach((bucket, index) => {
            const { tags = [] } = bucket;
            if (tags.indexOf(BucketStatus.WEB_LIVE) !== -1) {
                insertIndex = index;
            }
        });
        // if we found the live buckets, insert right after it, otherwise, push it on the end
        if (insertIndex !== -1) {
            clonedBuckets.splice(insertIndex + 1, 0, { __schedule_module_placeholder__: true });
            return clonedBuckets;
        }
    }

    clonedBuckets.push({ __schedule_module_placeholder__: true });
    return clonedBuckets;
}

// these are the buckets we don't want to render below the hero
const heroExcludeTags = [
    BucketLayout.HERO,
    BucketLayout.SCHEDULE_MODULE
];

class BucketListContainer extends Component {

    constructor(props) {
        super(props);
        this.showPromoOnHero = this.showPromoOnHero.bind(this);
    }

    showPromoOnHero() {
        const { page: { id }, showPromo } = this.props;
        return showPromo && id === 'espnplus';
    }

    render() {
        const {
            buckets,
            heroContentId,
            renderHeroInBucket,
            layoutOverride,
            pageType,
            hideHeaderLink: origHideHeaderLink,
            bamSubReady,
            page
        } = this.props;

        const curatedBuckets = insertScheduleModuleIntoBuckets(buckets.filter(({ type }) => (type !== 'hero')), pageType);
        const showPromo = this.showPromoOnHero();
        const { id } = page;

        return (
            <div className="bucket-list">
                <Helmet
                    style={[{
                        cssText: `
                        .dtc-watch__row--showAll .dtc-watch__item{
                            display:block !important;
                        }
                    `
                    }]}
                />
                {curatedBuckets.map((bucket, index) => {
                    // inserts the schedule module into a mobile only container depending on what page we're on
                    if (bucket.__schedule_module_placeholder__ === true) {
                        return <div className="mobile-tablet-only"><ScheduleModuleContainer contentLimit={3} /></div>;
                    }

                    // some pages such as the /watch/shows page do not have a hero
                    const bucketContents = bucket.contents.filter((content) =>
                        renderHeroInBucket || (id === 'espnplus' &&
                        ((bamSubReady && !showPromo && heroContentId !== content.id) ||
                        ((bamSubReady && showPromo) && content.id) ||
                        (!bamSubReady && content.id))) ||
                        (id !== 'espnplus' && heroContentId !== content.id)
                    );

                    const contentLength = bucketContents.length; // same as displayCount
                    if (!renderHeroInBucket && !showPromo &&
                        (
                            (contentLength === 1 && bucketContents[0].id === heroContentId) ||
                            (bucket.tags && _intersection(heroExcludeTags, bucket.tags).length > 0)
                        )
                    ) {
                        return null;
                    }

                    const hideHeaderLink = origHideHeaderLink && index === 0;

                    // make the link relative by chopping off the host
                    const { links: { web = '' } = {}, metadata } = bucket || {};
                    const link = web.replace(/https?:\/\/[^\/]*(?:\/watch)?/, '');
                    const bucketId = (metadata && metadata.bucketId) || bucket.id;
                    const groupId = metadata && metadata.groupId;
                    // Avoids duplicate key assignment error
                    const uniqueKey = `${bucket.id}${index}`;

                    return (
                        <Bucket
                            bucketId={bucketId}
                            hideHeaderLink={hideHeaderLink}
                            contents={bucketContents}
                            groupId={groupId}
                            iconHref={bucket.iconHref}
                            key={uniqueKey}
                            layout={bucket.layout}
                            layoutOverride={layoutOverride}
                            link={link}
                            metadata={bucket.metadata}
                            name={bucket.name}
                            pageType={pageType}
                            tags={bucket.tags}
                            type={bucket.type}
                            tier={index + 1}
                        />
                    );
                })}
            </div>
        );
    }
}

BucketListContainer.propTypes = {
    buckets: PropTypes.arrayOf(WatchPropTypes.bucket),
    heroContentId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    renderHeroInBucket: PropTypes.bool,
    pageType: PropTypes.string,
    layoutOverride: PropTypes.string,
    hideHeaderLink: PropTypes.bool,
    bamSubReady: PropTypes.bool,
    showPromo: PropTypes.bool,
    page: PropTypes.object
};

BucketListContainer.defaultProps = {
    buckets: [],
    renderHeroInBucket: false
};

const mapStateToProps = (state) => ({
    buckets: (state.page && state.page.buckets) || [],
    heroContentId: state.page.hero && state.page.hero.id,
    page: pageSelector(state),
    bamSubReady: bamSubReadySelector(state),
    showPromo: showPromoSelector(state)
});

export default connect(mapStateToProps)(BucketListContainer);



// WEBPACK FOOTER //
// ./app/common/page/buckets/BucketListContainer.js